/**
 * The vocabulary pass.
 *
 * JSON Schema checks a package's *shape*; it cannot check that a tag value is
 * one the vocabulary declares, because the `concept` axis is extended per
 * package. That check lives here, as a pure function over a parsed package, so
 * the validator CLI, the authoring repair loop, and the engine's load-time
 * checks all report the same errors from the same code.
 *
 * Every finding carries a JSON pointer, so the validator can report all of them
 * in one sweep rather than stopping at the first.
 */
import { VOCAB } from './generated/vocab.js';
import { TUTORIAL_ANCHORS } from './generated/tutorial-anchors.js';
/** Every axis name the vocabulary declares. */
export const isAxis = (axis) => Object.hasOwn(VOCAB, axis);
/** True for axes declared `kind: ordinal`, the only axes `max` and `min` accept. */
export const isOrdinalAxis = (axis) => isAxis(axis) && VOCAB[axis].kind === 'ordinal';
/** The values an axis declares. For an ordinal axis this is the low-to-high order. */
export const axisValues = (axis) => VOCAB[axis].values;
/**
 * Position of a value in an ordinal axis's order, or `-1`. The engine's `max`
 * and `min` predicates compare these.
 */
export const ordinalIndex = (axis, value) => VOCAB[axis].values.indexOf(value);
/**
 * True for a region named by the closed tutorial anchor vocabulary.
 *
 * The level schema types an anchor as a dotted-lowercase string rather than an
 * enum, because the anchor vocabulary carries its own version and would
 * otherwise have to be copied into `level.schema.json` on every bump. This is
 * where membership is decided, for the validator and the shell alike.
 */
export const isTutorialAnchor = (anchor) => Object.hasOwn(TUTORIAL_ANCHORS, anchor);
/**
 * Checks one axis/value pair. `extensions` supplies the package's declared
 * `conceptExtensions`; pass an empty set to check against the base vocabulary
 * alone (as the perk catalogue is checked, since perks are not mission-local).
 */
export function checkTagValue(axis, value, extensions, pointer) {
    if (!isAxis(axis)) {
        return {
            code: 'unknown-axis',
            pointer,
            axis,
            message: `Unknown tag axis '${axis}'. Packages may not introduce axes; the vocabulary is closed.`,
        };
    }
    if (VOCAB[axis].values.includes(value))
        return undefined;
    if (VOCAB[axis].extensible && extensions.has(value))
        return undefined;
    const extra = VOCAB[axis].extensible
        ? " and is not declared in this package's conceptExtensions"
        : '';
    return {
        code: 'unknown-tag-value',
        pointer,
        axis,
        value,
        message: `'${value}' is not a value of the '${axis}' axis${extra}.`,
    };
}
const isRecord = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
/** JSON pointer segments escape `~` and `/` (RFC 6901). */
const segment = (key) => String(key).replace(/~/g, '~0').replace(/\//g, '~1');
const pointerTo = (...parts) => parts.length === 0 ? '' : `/${parts.map(segment).join('/')}`;
/**
 * Checks every axis/value pair in a `tagSet` — a particle's tags, the
 * proposal's initial tags, or a perk's granted particle.
 */
export function checkTagSet(tags, extensions, basePointer, issues) {
    if (!isRecord(tags))
        return;
    for (const [axis, raw] of Object.entries(tags)) {
        const values = Array.isArray(raw) ? raw : [raw];
        values.forEach((value, index) => {
            if (typeof value !== 'string')
                return;
            const pointer = Array.isArray(raw)
                ? `${basePointer}/${segment(axis)}/${index}`
                : `${basePointer}/${segment(axis)}`;
            const issue = checkTagValue(axis, value, extensions, pointer);
            if (issue)
                issues.push(issue);
        });
    }
}
/**
 * Walks a predicate tree. Every leaf naming an axis is checked against the
 * vocabulary, and `max`/`min` are rejected on nominal axes — the operator is
 * meaningless without a declared order, so an authored rule using one would
 * silently never fire.
 */
function checkPredicate(predicate, extensions, basePointer, issues) {
    if (!isRecord(predicate))
        return;
    for (const [key, operand] of Object.entries(predicate)) {
        const pointer = `${basePointer}/${segment(key)}`;
        if (key === 'all' || key === 'any') {
            if (Array.isArray(operand)) {
                operand.forEach((child, index) => {
                    checkPredicate(child, extensions, `${pointer}/${index}`, issues);
                });
            }
            continue;
        }
        if (key === 'not') {
            checkPredicate(operand, extensions, pointer, issues);
            continue;
        }
        if (key === 'proposal') {
            // `proposal.has` is the only proposal leaf naming an axis.
            if (isRecord(operand) && isRecord(operand['has'])) {
                checkAxisLeaf(operand['has'], extensions, `${pointer}/has`, issues, false);
            }
            if (isRecord(operand) && typeof operand['attribution'] === 'string') {
                const issue = checkTagValue('attribution', operand['attribution'], extensions, `${pointer}/attribution`);
                if (issue)
                    issues.push(issue);
            }
            continue;
        }
        if (key === 'has' || key === 'none' || key === 'count') {
            checkAxisLeaf(operand, extensions, pointer, issues, false);
            continue;
        }
        if (key === 'max' || key === 'min') {
            checkAxisLeaf(operand, extensions, pointer, issues, true, key);
            continue;
        }
        if (key === 'distinct' || key === 'empty' || key === 'present') {
            checkAxisLeaf(operand, extensions, pointer, issues, false);
            continue;
        }
    }
}
function checkAxisLeaf(leaf, extensions, pointer, issues, ordinalOnly, operator) {
    if (!isRecord(leaf))
        return;
    const axis = leaf['axis'];
    if (typeof axis !== 'string')
        return;
    if (!isAxis(axis)) {
        issues.push({
            code: 'unknown-axis',
            pointer: `${pointer}/axis`,
            axis,
            message: `Unknown tag axis '${axis}'. Packages may not introduce axes; the vocabulary is closed.`,
        });
        return;
    }
    if (ordinalOnly && VOCAB[axis].kind !== 'ordinal') {
        issues.push({
            code: 'ordinal-operator-on-nominal-axis',
            pointer,
            axis,
            message: `'${operator}' is not permitted on '${axis}', which is declared nominal. Only ordinal axes declare the order these operators compare against.`,
        });
    }
    const value = leaf['value'];
    if (typeof value === 'string') {
        const issue = checkTagValue(axis, value, extensions, `${pointer}/value`);
        if (issue)
            issues.push(issue);
    }
}
/**
 * Checks every tag axis and value a package uses against the closed vocabulary
 * and the package's own `conceptExtensions`.
 *
 * Returns every issue found, never throwing and never stopping early.
 */
export function checkVocabulary(pack) {
    const issues = [];
    // conceptExtensions first: the rest of the pass is checked against them.
    const extensions = new Set();
    const rawExtensions = pack['conceptExtensions'];
    if (Array.isArray(rawExtensions)) {
        rawExtensions.forEach((entry, index) => {
            if (!isRecord(entry) || typeof entry['value'] !== 'string')
                return;
            const value = entry['value'];
            const pointer = pointerTo('conceptExtensions', index, 'value');
            if (VOCAB.concept.values.includes(value)) {
                issues.push({
                    code: 'concept-extension-shadows-vocabulary',
                    pointer,
                    axis: 'concept',
                    value,
                    message: `'${value}' is already a shared 'concept' value; a package may not redeclare it.`,
                });
                return;
            }
            if (extensions.has(value)) {
                issues.push({
                    code: 'duplicate-concept-extension',
                    pointer,
                    axis: 'concept',
                    value,
                    message: `'${value}' is declared more than once in conceptExtensions.`,
                });
                return;
            }
            extensions.add(value);
        });
    }
    const particles = pack['particles'];
    if (Array.isArray(particles)) {
        particles.forEach((particle, index) => {
            if (!isRecord(particle))
                return;
            checkTagSet(particle['tags'], extensions, pointerTo('particles', index, 'tags'), issues);
        });
    }
    const proposal = pack['proposal'];
    if (isRecord(proposal)) {
        checkTagSet(proposal['initialTags'], extensions, pointerTo('proposal', 'initialTags'), issues);
        if (typeof proposal['attribution'] === 'string') {
            const issue = checkTagValue('attribution', proposal['attribution'], extensions, pointerTo('proposal', 'attribution'));
            if (issue)
                issues.push(issue);
        }
    }
    const outcomes = pack['outcomes'];
    if (isRecord(outcomes)) {
        for (const [outcomeId, outcome] of Object.entries(outcomes)) {
            if (!isRecord(outcome))
                continue;
            const mutations = outcome['proposalMutations'];
            if (!Array.isArray(mutations))
                continue;
            mutations.forEach((mutation, index) => {
                if (!isRecord(mutation))
                    return;
                const base = pointerTo('outcomes', outcomeId, 'proposalMutations', index);
                const axis = mutation['op'] === 'setAttribution' ? 'attribution' : mutation['axis'];
                const value = mutation['value'];
                if (typeof axis !== 'string' || typeof value !== 'string')
                    return;
                const issue = checkTagValue(axis, value, extensions, `${base}/value`);
                if (issue)
                    issues.push(issue);
            });
        }
    }
    const cast = pack['cast'];
    if (Array.isArray(cast)) {
        cast.forEach((node, nodeIndex) => {
            if (!isRecord(node))
                return;
            const resolve = node['resolve'];
            if (isRecord(resolve) && Array.isArray(resolve['rules'])) {
                resolve['rules'].forEach((rule, ruleIndex) => {
                    if (!isRecord(rule))
                        return;
                    checkPredicate(rule['when'], extensions, pointerTo('cast', nodeIndex, 'resolve', 'rules', ruleIndex, 'when'), issues);
                });
            }
            const chatter = node['chatter'];
            if (Array.isArray(chatter)) {
                chatter.forEach((pool, poolIndex) => {
                    if (!isRecord(pool) || pool['when'] === undefined)
                        return;
                    checkPredicate(pool['when'], extensions, pointerTo('cast', nodeIndex, 'chatter', poolIndex, 'when'), issues);
                });
            }
        });
    }
    const schedule = pack['schedule'];
    if (Array.isArray(schedule)) {
        schedule.forEach((event, index) => {
            if (!isRecord(event) || event['when'] === undefined)
                return;
            checkPredicate(event['when'], extensions, pointerTo('schedule', index, 'when'), issues);
        });
    }
    const puppets = pack['puppets'];
    if (Array.isArray(puppets)) {
        puppets.forEach((puppet, index) => {
            if (!isRecord(puppet))
                return;
            const voice = puppet['voice'];
            if (!isRecord(voice))
                return;
            for (const key of ['requires', 'forbids']) {
                if (voice[key] === undefined)
                    continue;
                checkPredicate(voice[key], extensions, pointerTo('puppets', index, 'voice', key), issues);
            }
        });
    }
    const objectives = pack['objectives'];
    if (isRecord(objectives)) {
        if (objectives['win'] !== undefined) {
            checkPredicate(objectives['win'], extensions, pointerTo('objectives', 'win'), issues);
        }
        const lose = objectives['lose'];
        if (Array.isArray(lose)) {
            lose.forEach((condition, index) => {
                if (!isRecord(condition))
                    return;
                checkPredicate(condition['when'], extensions, pointerTo('objectives', 'lose', index, 'when'), issues);
            });
        }
    }
    return issues;
}
//# sourceMappingURL=vocabulary.js.map